import { ChangeDetectionStrategy, Component, inject, resource, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ArchiveService } from '../../../core/services/archive.service';
import type { ArchiveFile } from '../../../shared/interfaces';
import { AuthService } from '../../../core/auth/auth.service';
import { TenantService } from '../../../core/services/tenant.service';
import { StorageService } from '../../../core/services/storage.service';

export interface ArchiveFileView extends Omit<ArchiveFile, 'updatedAt'> {
  updatedAt: Date;
}

@Component({
  selector: 'app-data-download',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './data-download.html',
  styleUrl: './data-download.scss'
})
export class DataDownloadComponent {
  private readonly authService = inject(AuthService);
  private readonly tenantService = inject(TenantService);
  private readonly storageService = inject(StorageService);
  private readonly archiveService = inject(ArchiveService);
  private readonly snackBar = inject(MatSnackBar);

  public readonly displayedColumns: string[] = ['targetMonth', 'siteName', 'size', 'updatedAt', 'actions'];
  public readonly isDownloading = signal(false);
  public readonly downloadingPath = signal<string | null>(null);

  public readonly archivesResource = resource({
    defaultValue: [] as ArchiveFileView[],
    params: () => ({
      role: this.authService.userRole(),
      organizationId: this.tenantService.organization()?.id,
      siteId: this.authService.userSiteId()
    }),
    loader: async ({ params }): Promise<ArchiveFileView[]> => {
      if (!params.role) return [];
      const archives = await this.archiveService.fetchArchives(params.role, params.organizationId, params.siteId);
      return archives.map(a => ({
        ...a,
        updatedAt: a.updatedAt.toDate()
      }));
    }
  });



  public formatBytes(bytes: number, decimals = 2): string {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  public async downloadFile(file: ArchiveFileView): Promise<void> {
    if (this.isDownloading()) return;

    this.isDownloading.set(true);
    this.downloadingPath.set(file.fullPath);

    try {
      const url = await this.storageService.getDownloadUrl(file.fullPath);
      
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      this.snackBar.open('ダウンロードを開始しました', '閉じる', { duration: 3000 });
    } catch (e: any) {
      console.error('Download error:', e);
      this.snackBar.open('データのダウンロードに失敗しました', '閉じる', { duration: 5000 });
    } finally {
      this.isDownloading.set(false);
      this.downloadingPath.set(null);
    }
  }
}
